(function () {
  if (customElements.get('clinical-doses-slider')) return;

  // Desktop coverflow vertical offsets, derived from Figma 16623:20154.
  const Y_OFFSETS = [0, 144.84, 265.27, 358.33];
  const MAX_VISIBLE = Y_OFFSETS.length - 1;
  const POS_CLASS_PREFIX = 'clinical-doses__card--pos-';
  const MAX_POS = 7;
  const CENTER_POS = 4;

  // Mobile slider step (px) between adjacent card centers, derived from
  // Figma 18150:12199: focused card is 190 wide, neighbors are scaled to
  // 160 (0.84). Center-to-center = 95 + 12 gap + 80 = 187.
  const MOBILE_STEP_X = 187;

  const WHEEL_THROTTLE_MS = 380;
  const AUTOPLAY_DEFAULT_MS = 4000;
  const SWIPE_THRESHOLD = 40;

  class ClinicalDosesSlider extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-cd-card]'));
      if (this.cards.length === 0) return;

      this.list = this.querySelector('.clinical-doses__cards');
      this.focused = Math.floor(this.cards.length / 2);
      this.classList.add('clinical-doses-slider--js');

      this.cards.forEach((card, i) => {
        card.addEventListener('click', () => {
          this.pauseAutoplayBriefly();
          this.setFocus(i);
        });
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.pauseAutoplayBriefly();
            this.setFocus(i);
          }
        });
      });

      this.mobileAutoplayEnabled = this.dataset.mobileAutoplay !== 'false';
      this.mobileAutoplayMs = parseInt(this.dataset.mobileAutoplayInterval, 10) || AUTOPLAY_DEFAULT_MS;
      this.desktopAutoplayEnabled = this.dataset.desktopAutoplay === 'true';
      this.desktopAutoplayMs = parseInt(this.dataset.desktopAutoplayInterval, 10) || AUTOPLAY_DEFAULT_MS;

      this.applyFocus();
      this.bindWheel();
      this.bindTouch();
      this.bindAutoplayLifecycle();
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.viewportObserver) this.viewportObserver.disconnect();
    }

    isMobile() {
      return window.matchMedia('(max-width: 989px)').matches;
    }

    setFocus(index) {
      if (index === this.focused) return;
      this.focused = index;
      this.applyFocus();
    }

    advance(direction) {
      const N = this.cards.length;
      const next = (this.focused + direction + N) % N;
      this.setFocus(next);
    }

    applyFocus() {
      const N = this.cards.length;
      this.cards.forEach((card, i) => {
        let signed = (((i - this.focused) % N) + N) % N;
        if (signed > N / 2) signed -= N;
        const absDist = Math.abs(signed);

        const yIndex = Math.min(absDist, Y_OFFSETS.length - 1);
        const y = Math.sign(signed) * Y_OFFSETS[yIndex];
        const pos = Math.max(1, CENTER_POS - absDist);

        const prevSigned = card.__cdSigned;
        const wrapped = typeof prevSigned === 'number'
          && Math.abs(signed - prevSigned) > N / 2;

        card.style.setProperty('--cd-y', y + 'px');

        if (wrapped) {
          // Teleport this card just off-screen on the side it should re-enter
          // from, then animate to its real signed position. Without this, the
          // wrapped card would slide visibly across the whole rail.
          // direction matches the rail motion: forward wrap (signed grew)
          // → card enters from the right (interim = signed + 1).
          const direction = signed > prevSigned ? 1 : -1;
          const interim = signed + direction;
          const targetSigned = signed;
          card.style.transition = 'none';
          card.style.setProperty('--cd-x', interim * MOBILE_STEP_X + 'px');
          // Force reflow so the browser commits the no-transition jump,
          // then re-enable transition and animate to the real position.
          // eslint-disable-next-line no-unused-expressions
          card.offsetWidth;
          requestAnimationFrame(() => {
            card.style.transition = '';
            card.style.setProperty('--cd-x', targetSigned * MOBILE_STEP_X + 'px');
          });
        } else {
          card.style.setProperty('--cd-x', signed * MOBILE_STEP_X + 'px');
        }
        card.__cdSigned = signed;

        card.classList.toggle('is-focused', i === this.focused);
        card.dataset.cdHidden = absDist > MAX_VISIBLE ? 'true' : 'false';

        for (let p = 1; p <= MAX_POS; p++) {
          card.classList.remove(POS_CLASS_PREFIX + p);
        }
        card.classList.add(POS_CLASS_PREFIX + pos);
      });
    }

    bindWheel() {
      if (!this.list) return;
      this.lastWheel = 0;
      this.list.addEventListener('wheel', (e) => {
        if (this.isMobile()) return;
        const now = Date.now();
        if (now - this.lastWheel < WHEEL_THROTTLE_MS) {
          e.preventDefault();
          return;
        }
        if (Math.abs(e.deltaY) < 4) return;
        this.lastWheel = now;
        e.preventDefault();
        this.pauseAutoplayBriefly();
        this.advance(e.deltaY > 0 ? 1 : -1);
      }, { passive: false });
    }

    bindTouch() {
      if (!this.list) return;
      let startX = 0;
      let startY = 0;
      let tracking = false;

      this.list.addEventListener('touchstart', (e) => {
        if (!this.isMobile()) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
      }, { passive: true });

      this.list.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        if (Math.abs(dx) < SWIPE_THRESHOLD) return;
        if (Math.abs(dy) > Math.abs(dx)) return; // vertical swipe → ignore
        this.advance(dx < 0 ? 1 : -1);
      }, { passive: true });

      this.list.addEventListener('touchcancel', () => {
        tracking = false;
      }, { passive: true });
    }

    bindAutoplayLifecycle() {
      if (this.list) {
        this.list.addEventListener('touchstart', () => this.pauseAutoplayBriefly(), { passive: true });
        this.addEventListener('mouseenter', () => this.pauseAutoplayBriefly());
      }

      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.startAutoplay();
            } else {
              this.stopAutoplay();
            }
          });
        }, { threshold: 0.2 });
        this.viewportObserver.observe(this);
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      });
    }

    autoplayConfigForViewport() {
      if (this.isMobile()) {
        return {
          enabled: this.mobileAutoplayEnabled,
          interval: this.mobileAutoplayMs,
        };
      }
      return {
        enabled: this.desktopAutoplayEnabled,
        interval: this.desktopAutoplayMs,
      };
    }

    startAutoplay() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const cfg = this.autoplayConfigForViewport();
      if (!cfg.enabled) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => this.advance(1), cfg.interval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    pauseAutoplayBriefly() {
      const cfg = this.autoplayConfigForViewport();
      this.stopAutoplay();
      if (this.resumeTimer) clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.startAutoplay(), cfg.interval * 1.5);
    }
  }

  customElements.define('clinical-doses-slider', ClinicalDosesSlider);
})();
